import React from "react";
import {
  FormHelperText,
  FormHelperTextProps,
  Typography,
  TypographyProps,
} from "@mui/material";
import { styled } from "@mui/material/styles";

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: (theme.vars || theme).palette.error.main,
}));

const StyledFormHelperText = styled(FormHelperText)<{ greyColor?: boolean }>(
  ({ theme, greyColor }) => ({
    marginLeft: 0,
    marginRight: 0,
    ...(greyColor && {
      color: (theme.vars || theme).palette.text.secondary,
      "&.Mui-error": {
        color: theme.palette.text.secondary, // Override red error color to grey
      },
    }),
  })
);

const InlineFormHelperText = styled(FormHelperText)(() => ({
  display: "inline-block",
  margin: 0,
}));

type RpFormErrorVariant = "helper" | "text" | "inline";

interface RpFormErrorProps
  extends Omit<
    FormHelperTextProps,
    "children" | "error" | "component" | "variant"
  > {
  error?: React.ReactNode;
  show?: boolean;
  className?: string;
  variant?: RpFormErrorVariant;
  component?: React.ElementType;
  greyColor?: boolean;
}

const TextError = StyledTypography as React.ElementType;
const HelperError = StyledFormHelperText as React.ElementType;
const InlineHelperError = InlineFormHelperText as React.ElementType;

const RpFormError = ({
  error,
  show = true,
  className = "",
  variant = "helper", // helper, text, inline
  component = "div",
  greyColor = false, // New prop to show error in grey instead of red
  ...rest
}: RpFormErrorProps) => {
  if (!show || !error) return null;

  const errorMessage = String(error);

  switch (variant) {
    case "text":
      return (
        <TextError
          component={component}
          className={className}
          {...(rest as TypographyProps)}
        >
          {errorMessage}
        </TextError>
      );

    case "inline":
      return (
        <InlineHelperError
          error={!greyColor} // Only show error styling if not grey
          component={component}
          className={className}
          {...rest}
        >
          {errorMessage}
        </InlineHelperError>
      );

    case "helper":
    default:
      return (
        <HelperError
          error={!greyColor} // Only show error styling if not grey
          greyColor={greyColor}
          component={component}
          className={className}
          {...rest}
        >
          {errorMessage}
        </HelperError>
      );
  }
};

export default RpFormError;
